import type { NitroFetchOptions } from 'nitropack';
import { useAsyncData } from 'nuxt/app';
import type { CookieOptions } from 'nuxt/app';
import type { ApiResponse, SendGetOptions, SendResponse, SendApiResponse, SendPostOptions } from '~/types/api';

import { useMainStore } from '@/stores/useMainStore';
import { useRootStore } from '@/stores/useRootStore';
import { useAuthStore } from '@/stores/useAuthStore';
import CryptoJS from 'crypto-js';

import { appLoginCall } from '@/composables/useAuth';

import { traceLogUserIdIVKey, traceLogUserIdKey } from '@/constants/key';

import { isMobileApp , isOnlyMobilePage} from '@/utils/common';

const isSSR = (): boolean => {
  console.log('isSSR :::: ', import.meta.server)
  console.log('isSSR :::: ', import.meta.client)
  console.log('isSSR :::: ', useNuxtApp().payload.serverRendered)
  return import.meta.server || (import.meta.client && useNuxtApp().payload.serverRendered) ? true : false;
};

/**
 * 에러 메세지 구하기
 * @param error 
 * @returns 
 */
export const getErrorMessage = (error: string | Record<string, any>): string => {
  let message = "";
  let resultCode = '';
  if (typeof error === 'object') {
    resultCode = error?.RESULT_CODE;
  } else {
    resultCode = error;
  }
  if (resultCode === "10") {
    message = "API 시스템 오류입니다. 관리자에게 문의해 주세요."
  } else if (resultCode === "500") {
    message = "로그인 정보를 찾을 수 없습니다. 다시 로그인해 주세요.";
  } else {
    message = "시스템 오류입니다. 잠시 후 다시 시도해 주세요.";
  }

  return message;
}

/**
 * API 호출처리를 위한 로직
 * @returns 
 */
export const api = () => {
  // 로그아웃이 필요한 에러코드
  const logoutCode = ["702","703","704","705","706","707","708"];
  const rootStore = useRootStore();
  const store = useMainStore();
  const authStore = useAuthStore();
  const memeberStore = useMemberStore();
  const config = useRuntimeConfig();
  
  const cookieDefaultOption: CookieOptions & { readonly?: false | undefined } = {
    path: '/',
    //domain: process.env.VITE_ENV_TYPE === 'local' ? 'localhost' : '.ttobakcare.com',
    secure: config.public.VITE_ENV_TYPE === 'local' ? false : true,
    sameSite: config.public.VITE_ENV_TYPE === 'local' ? 'lax' : 'none' as (boolean | "none" | "lax" | "strict" | undefined),
    //decode: undefined,
    //encode: undefined,
    //default: undefined,
    watch: true,
    readonly: false as (false | undefined),
  };
  
  // if (config.public.VITE_ENV_TYPE !== 'local') {
  //   cookieDefaultOption.domain = '.ttobakcare.com';
  // }

  if (config.public.VITE_ENV_TYPE !== 'local') {
    if (import.meta.env.SSR) {
      // SSR:::::::::::
      const headers = useRequestHeaders();
      const host = headers['host'] || '';
  
      if (host.includes('ttobakcare.com')) {
        cookieDefaultOption.domain = '.ttobakcare.com';
      } else if (host.includes('desimone.co.kr')) {
        cookieDefaultOption.domain = '.desimone.co.kr';
      } else {
        cookieDefaultOption.domain = host;
      }
    } else {
      // CSR:::::::::::
      const currentDomain = window.location.hostname;
  
      if (currentDomain.endsWith('.ttobakcare.com')) {
        cookieDefaultOption.domain = '.ttobakcare.com';
      } else if (currentDomain.endsWith('.desimone.co.kr')) {
        cookieDefaultOption.domain = '.desimone.co.kr';
      } else {
        cookieDefaultOption.domain = currentDomain;
      }
    }
  }
  
  const userAgent = navigator ? navigator.userAgent : useRequestHeaders()['user-agent'] as string;
  const refreshTokenRef = useCookie('REFRESH_TOKEN', cookieDefaultOption);
  const userIdRef = useCookie('USER_ID', cookieDefaultOption); 
  const userIpRef = useCookie('USER_IP', cookieDefaultOption);
  const afterLoginUrlRef = useCookie('afterLoginUrl', cookieDefaultOption);
  
  const expireDate = new Date();
  expireDate.setDate(expireDate.getDate() + 30);
  
  const browserCookieRef = useCookie('BrowserCookie', {
    ...cookieDefaultOption,
    expires: expireDate,
  });
  
  const appSessIdRef = useCookie('APPSESSID');
  const webSessIdRef = useCookie('APP_SESSID');
  
  // 로그아웃 처리
  const logoutInApi = (response: ApiResponse) => {
    appSessIdRef.value = null;
    webSessIdRef.value = null;
    refreshTokenRef.value = null;
    userIdRef.value = null;
    afterLoginUrlRef.value = null;
    
    authStore.clearAuthenToken();
    memeberStore.clearMemberInfo();
      
    // 앱내에서 702 오류(중복 로그인) 발생하였을 경우 앱 로그인 호출
    if (isMobileApp() && response.RESULT_CODE === '702') appLoginCall(response.RESULT_CODE);
    
    return response;
  };
  
  // 2xx 응답 후처리
  const responseActions = async (response: ApiResponse, url: string, options: NitroFetchOptions<string>): Promise<ApiResponse> => {
    let webFlag = false;
    
    webFlag = userAgent.match(/vsl/i) !== null ? false : true;
    
    if (!webFlag) return Promise.resolve(response);
    
    if (logoutCode.includes(response.RESULT_CODE)) {
      return Promise.resolve(logoutInApi(response));
    } 

    if (response.RESULT_CODE !== '700') {
      return Promise.resolve(response);;
    }
    
    if (!refreshTokenRef.value) return Promise.resolve(logoutInApi(response));
    
    // JWT 토큰 갱신
    const resultToken = await getAccessToken();
    if (!resultToken) {
      return Promise.resolve(logoutInApi(response));
    }
    
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${resultToken.DATA.encodedJwt}`,
    };
    
    return $fetch<ApiResponse>(url, options);
  };
  
  /**
   * api 호출 
   * @param url string
   * @param options NitroFetchOptions<string> api 옵션
   * @param showDefaultLoading boolean 로딩 상태 노출여부
   * @returns Promise<ApiResponse>
   */
  const callApi = async (url: string, options: NitroFetchOptions<string>, showDefaultLoading: boolean = true): Promise<ApiResponse> => {
    let retryCount = 0;
    let response: ApiResponse | null = null;
    
    const retryMax = store.apiReload ? 2 : 0; // 에러 발생 시 재실행 횟수
    
    if (import.meta.client && showDefaultLoading) {
      rootStore.showLoading();
    } 
    
    while (retryCount <= retryMax) {
      try {
        response = await $fetch<ApiResponse>(url, options);
        break;
      } catch (err: any) {
        retryCount++;
        console.error('fetch', err)
        
        if (retryCount >= retryMax + 1) {
          if (import.meta.client && showDefaultLoading) {
            rootStore.hideLoading();
          }
          throw err;
        }
      } 
    }
    
    if (import.meta.client && showDefaultLoading) {
      rootStore.hideLoading();
    }
    
    if (import.meta.client && showDefaultLoading) {
      rootStore.showLoading();
    }
    
    // 2xx 응답 후처리
    try {
      if (response) response = await responseActions(response, url, options);
    } catch (error) {
      console.error('callApi', error);
      throw error;
    } finally {
      if (import.meta.client && showDefaultLoading) {
        rootStore.hideLoading();
      }    
    }
    
    if (response === null) {
      throw new Error('no response');
    }

    return response;
  };
  
  /**
   * API GET 호출 (setup 내에서 사용)
   * - SSR, CSR 모두 사용 가능
   * - SSR에서 서버에서 데이터를 가져와야 할 경우 사용함
   * @param options SendGetOptions
   * @returns Promise<SendResponse>
   */
  const sendGetInSetup = async (options: SendGetOptions): Promise<SendResponse> => {
    const { 
      url, data = {} as Record<string, any>, showDefaultLoading = true, apiReload = true,
      immediate = true, watch = [], key = '',
    } = options;
    
    // PC인경우 API 호출을 하지 않음
    if (!isOnlyMobilePage() && isPcWeb()) {
      return {
        data: ref(null),
        error: ref(null)
      };
    }
    
    if (import.meta.server && showDefaultLoading) rootStore.showLoading();

    store.updateApiReload(apiReload);

    // 쿠키 가져오기
    const userIp = userIpRef.value ?? '';
    const userId = userIdRef.value ?? '';
    const browserCookie = useCookie('BrowserCookie').value ?? '';
    
    const cleanUserId = userId ? userId.replace(/\s+/g, '') : '';
    const encryptionKey = traceLogUserIdKey();
    const iv = traceLogUserIdIVKey();

    // AES 암호화 처리
    const keyParsed = CryptoJS.enc.Utf8.parse(encryptionKey);
    const ivParsed = CryptoJS.enc.Utf8.parse(iv);
    const encrypted = CryptoJS.AES.encrypt(cleanUserId, keyParsed, {
      iv: ivParsed,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC,
    });
    
    const encryptedString = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    let token = '';
    
    if (isMobileApp()) {
      if (authStore.authToken) {
        token = authStore.authToken;
      } else if (appSessIdRef.value) {
        token = appSessIdRef.value;
      }
    } else {
      if (authStore.authToken) token = authStore.authToken;
    }
    
    const requestConfig = {
      method: 'GET' as 'GET',
      headers: {
        "PHPSESSID": '',  // 로그인 세션 ID를 가져오는 함수 정의 필요
        "BrowserCookie": browserCookie,
        "Authorization": token ? `Bearer ${token}` : '',
        "IP": userIp || '',
        "UserId": encryptedString,
        "Poc": 'pc',
      },
      params: data,
    };
    
    const reqKey = key ? key : url.split('/').pop() as string;
    
    const response = await useAsyncData(reqKey, 
      async () => {
        try {
          return await callApi(url, requestConfig, isSSR() ? showDefaultLoading : false);
        } catch (error) {
          throw error;
        } 
      },
      {
        immediate,
        watch,
      }
    );
    
    if (import.meta.server && showDefaultLoading) rootStore.hideLoading();

    return response;
  };
  
  /**
   * API GET 호출
   * - 클라이언트에서 호출할 경우 사용
   * @param options SendGetOptions
   * @returns Promise<SendApiResponse> 
   */
  const sendGet = async (options: SendGetOptions): Promise<SendApiResponse> => {
    const { 
      url, data: params = {} as Record<string, any>, showDefaultLoading = true, apiReload = true,
    } = options;
    
    // PC인경우 API 호출을 하지 않음
    if (!isOnlyMobilePage() && isPcWeb()) {
      return {
        data: null,
        error: null
      };
    }
    
    /* if (showDefaultLoading) {
      console.log('sendGet', 'loading');
      rootStore.showLoading();
    } */

    store.updateApiReload(apiReload);

    // 쿠키 가져오기
    const userIp = userIpRef.value ?? '';// useCookie('USER_IP').value;
    const userId = userIdRef.value ?? '';// useCookie('USER_ID').value;

    const cleanUserId = userId ? userId.replace(/\s+/g, '') : '';
    const encryptionKey = traceLogUserIdKey();
    const iv = traceLogUserIdIVKey();

    // AES 암호화 처리
    const keyParsed = CryptoJS.enc.Utf8.parse(encryptionKey);
    const ivParsed = CryptoJS.enc.Utf8.parse(iv);
    const encrypted = CryptoJS.AES.encrypt(cleanUserId, keyParsed, {
      iv: ivParsed,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC,
    });
    
    const encryptedString = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    let token = '';
    
    if (isMobileApp()) {
      if (authStore.authToken) {
        token = authStore.authToken;
      } else if (appSessIdRef.value) {
        token = appSessIdRef.value;
      }
    } else {
      if (authStore.authToken) token = authStore.authToken;
    }
    
    const requestConfig = {
      method: 'GET' as 'GET',
      headers: {
        "PHPSESSID": '',  // 로그인 세션 ID를 가져오는 함수 정의 필요
        "BrowserCookie": browserCookieRef.value ?? '',
        "Authorization": token ? `Bearer ${token}` : '',
        "IP": userIp || '',
        "UserId": encryptedString,
        "Poc": 'pc',
      },
      params
    };
    
    let data: ApiResponse | null = null;
    let error: unknown | null = null;
    
    try {
      data = await callApi(url, requestConfig, showDefaultLoading);
    } catch (err) {
      //throw err;
      console.error('sendGeta', err);
      error = err;
    } 
    
    return {
      data,
      error
    };
  };
  
  /**
   * API POST 호출 (setup 내에서 사용)
   * - SSR, CSR 모두 사용 가능
   * - SSR에서 서버에서 데이터를 가져와야 할 경우 사용함
   * @param options SendPostOptions
   * @returns Promise<SendResponse>
   */
  const sendPostInSetup = async (options: SendPostOptions): Promise<SendResponse> => {    
    const { 
      url, data = {} as Record<string, any>, showDefaultLoading = true, 
      requestConfig = {} as Record<string, any>, 
      apiReload = true, immediate = true, watch = [], key = '', 
    } = options;
    
    // PC인경우 API 호출을 하지 않음
    if (!isOnlyMobilePage() && isPcWeb()) {
      return {
        data: ref(null),
        error: ref(null)
      };
    }

    if (import.meta.server && showDefaultLoading) rootStore.showLoading();

    store.updateApiReload(apiReload);

    // 쿠키 가져오기
    const userIp = userIpRef.value ?? '';
    const userId = userIdRef.value ?? '';

    const cleanUserId = userId ? userId.replace(/\s+/g, '') : '';
    const encryptionKey = traceLogUserIdKey();
    const iv = traceLogUserIdIVKey();

    // AES 암호화 처리
    const keyParsed = CryptoJS.enc.Utf8.parse(encryptionKey);
    const ivParsed = CryptoJS.enc.Utf8.parse(iv);
    const encrypted = CryptoJS.AES.encrypt(cleanUserId, keyParsed, {
      iv: ivParsed,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC,
    });
    
    const encryptedString = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    let token = '';
    
    if (isMobileApp()) {
      if (authStore.authToken) {
        token = authStore.authToken;
      } else if (appSessIdRef.value) {
        token = appSessIdRef.value;
      }
    } else {
      if (authStore.authToken) token = authStore.authToken;
    }
    
    requestConfig.method = 'POST';
    requestConfig.headers = {
      "PHPSESSID":'',
      "BrowserCookie": browserCookieRef.value ?? '',
      "Authorization": token ? `Bearer ${token}` : '',
      "IP": userIp || '',
      "UserId": encryptedString,
      "Poc": 'pc',
    };
    
    requestConfig.httpAgent = {
      rejectUnauthorized: false
    };
    
    requestConfig.body = data;
    
    const reqKey = key ? key : url.split('/').pop() as string;
    
    const response = await useAsyncData(reqKey, 
      async () => {
        try {
          return await callApi(url, requestConfig, showDefaultLoading);
        } catch (error) {
          console.error('sendPostInSetup', error);
          throw error;
        } 
      },
      {
        immediate,
        watch,
      }
    );
      
    if (import.meta.server && showDefaultLoading) rootStore.hideLoading();

    return response;
  };
  
  /**
   * API POST 호출
   * - 클라이언트에서 호출할 경우 사용
   * @param options SendPostOptions
   * @returns Promise<SendApiResponse>
   */
  const sendPost = async (options: SendPostOptions): Promise<SendApiResponse> => {
    const { 
      url, data: body = {} as Record<string, any>, showDefaultLoading = true, 
      requestConfig = {} as Record<string, any>, 
      apiReload = true, immediate = true, watch = [] 
    } = options;
    
    /* if (showDefaultLoading) {
      console.log('sendPost', 'loading');
      rootStore.showLoading();
    } */

    // PC인경우 API 호출을 하지 않음
    if (!isOnlyMobilePage() && isPcWeb()) {
      return {
        data: null,
        error: null
      };
    }

    store.updateApiReload(apiReload);

    // 쿠키 가져오기
    const userIp = userIpRef.value ?? '';
    const userId = userIdRef.value ?? '';
  
    const cleanUserId = userId ? userId.replace(/\s+/g, '') : '';
    const encryptionKey = traceLogUserIdKey();
    const iv = traceLogUserIdIVKey();

    // AES 암호화 처리
    const keyParsed = CryptoJS.enc.Utf8.parse(encryptionKey);
    const ivParsed = CryptoJS.enc.Utf8.parse(iv);
    const encrypted = CryptoJS.AES.encrypt(cleanUserId, keyParsed, {
      iv: ivParsed,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC,
    });
    
    const encryptedString = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    let token = '';
    
    if (isMobileApp()) {
      if (authStore.authToken) {
        token = authStore.authToken;
      } else if (appSessIdRef.value) {
        token = appSessIdRef.value;
      }
    } else {
      if (authStore.authToken) token = authStore.authToken;
    }
    
    requestConfig.method = 'POST';
    requestConfig.headers = {
      "PHPSESSID":'',
      "BrowserCookie": browserCookieRef.value ?? '',
      "Authorization": token ? `Bearer ${token}` : '',
      "IP": userIp || '',
      "UserId": encryptedString,
      "Poc": 'pc',
      "Accept": "application/json, text/plain, */*",
    };
    
    requestConfig.httpAgent = {
      rejectUnauthorized: false
    };
    
    requestConfig.body = body;
    
    let data: ApiResponse | null = null;
    let error: unknown | null = null;
    
    try {
      data = await callApi(url, requestConfig, showDefaultLoading);
    } catch (err) {
      console.error('sendPost', err);
      error = err;
    } finally {
      /* if (showDefaultLoading) {
        console.log('sendPost', 'done');
        rootStore.hideLoading();
      } */
    }
    
    return {
      data,
      error
    };
  };
  
  /**
   * access token, refresh token 구하기
   * @returns ApiResponse | null
   */
  const getAccessToken = async (): Promise<ApiResponse | null> => {
    //console.log('refreshToken - cookie', refreshTokenRef.value);
    
    const body = {
      userId: userIdRef.value ?? '',
      refreshToken: refreshTokenRef.value ?? '',
    };
    
    let result: ApiResponse | null = null;
    
    const con = import.meta.server ? config : config.public;
    if (import.meta.client) {
      const { data } = await sendPost({
        url: `${con.VITE_STORE_AUTH_API_URL}/${config.public.VITE_STORE_AUTH_VER}/store/auth/jwt-refresh`,
        data: body,
        showDefaultLoading: false,
      });
      result = data;
    } else {
      const { data } = await sendPostInSetup({
        url: `${con.VITE_STORE_AUTH_API_URL}/${config.public.VITE_STORE_AUTH_VER}/store/auth/jwt-refresh`,
        data: body,
        showDefaultLoading: false,
      });
      
      result = data.value;
    }
    
    if (result?.RESULT_CODE === '0') {
      authStore.saveAuthenToken(result.DATA.encodedJwt);
      refreshTokenRef.value = result.DATA.refreshToken;
      
      //console.log('authToken', authStore.authToken);
      //console.log('refreshToken', refreshTokenRef.value)
      // path, domain, secure, sameSite 는 기본 설정되어 있음

      return result;
    } else {
      return null;
    }
  };
  
  return {
    isSSR,
    config,
    getAccessToken,
    callApi,
    sendGetInSetup,
    sendGet,
    sendPostInSetup,
    sendPost,
  }
};
